import {
  normalizeQuestionType,
  questionTypeLabel,
} from "../../quick-sheets/normalizeQuestionType";
import { sanitizeFilename } from "../../ppt/sanitizeFilename";
import { normalizePdfText } from "../normalizePdfText";

function coerceText(value) {
  return normalizePdfText(value);
}

function buildQuestionSlide(question) {
  const questionType = normalizeQuestionType(question.questionType);

  return {
    kind: "question",
    number: question.number,
    questionType,
    questionTypeLabel: questionTypeLabel(questionType),
    prompt: coerceText(question.prompt),
    choices: (question.choices ?? []).map((choice) => ({
      id: choice.id,
      label: coerceText(choice.label),
    })),
    answerLines: question.answerLines ?? 3,
    matchingPairs: (question.matchingPairs ?? []).map((pair) => ({
      left: coerceText(pair.left),
      right: coerceText(pair.right),
    })),
    blankPrompt: coerceText(question.blankPrompt ?? question.prompt),
  };
}

function buildTeacherAppendixSlides(lesson) {
  const slides = [
    {
      kind: "section",
      title: "Teacher Sheet",
      body: "Teacher-only document. Do not distribute with student papers.",
      pageBreakBefore: false,
    },
  ];

  if (lesson.usageGuidance?.length) {
    slides.push({
      kind: "bullets",
      title: "Usage Guidance",
      bullets: lesson.usageGuidance.map((row) =>
        coerceText(`${row.label} ${row.body}`),
      ),
    });
  }

  if (lesson.quickCheckItems?.length) {
    slides.push({
      kind: "bullets",
      title: "Quick Check",
      bullets: lesson.quickCheckItems.map((item) => coerceText(item)),
    });
  }

  if (lesson.optionalTip?.title) {
    const tipBullets = [coerceText(lesson.optionalTip.body)].filter(Boolean);
    slides.push({
      kind: "bullets",
      title: coerceText(lesson.optionalTip.title),
      bullets: tipBullets,
      subtitle: lesson.optionalTip.answerNote
        ? coerceText(lesson.optionalTip.answerNote)
        : undefined,
    });
  }

  const supporting = lesson.supporting;
  if (supporting) {
    slides.push({
      kind: "section",
      title: "Supporting Elements",
    });

    if (supporting.example) {
      slides.push({
        kind: "bullets",
        title: "Example",
        bullets: [coerceText(supporting.example)],
      });
    }

    if (supporting.misconceptionTitle) {
      slides.push({
        kind: "bullets",
        title: coerceText(supporting.misconceptionTitle),
        bullets: [coerceText(supporting.misconceptionBody)].filter(Boolean),
      });
    }

    if (supporting.extensionActivity) {
      slides.push({
        kind: "bullets",
        title: "Extension Activity",
        bullets: [coerceText(supporting.extensionActivity)],
      });
    }
  }

  return slides;
}

function buildAnswerKeySlides(lesson) {
  const entries =
    lesson.answerKey?.entries.filter(
      (entry) =>
        entry.correctAnswer ||
        entry.matchingPairs?.length ||
        entry.blankAnswers?.length,
    ) ?? [];

  if (entries.length === 0) {
    return [];
  }

  const slides = [
    {
      kind: "section",
      title: "Answer Key",
      body: "Teacher reference - correct answers and marking guidance.",
      pageBreakBefore: true,
    },
  ];

  for (const entry of entries) {
    slides.push({
      kind: "answer-entry",
      number: entry.questionNumber,
      questionType: entry.questionType,
      correctAnswer: coerceText(entry.correctAnswer),
      explanation: coerceText(entry.explanation),
      markingGuidance: coerceText(entry.markingGuidance),
      matchingPairs: entry.matchingPairs ?? [],
      blankAnswers: entry.blankAnswers ?? [],
    });
  }

  if (lesson.answerKey?.teacherNote) {
    slides.push({
      kind: "bullets",
      title: "Teacher Note",
      bullets: [coerceText(lesson.answerKey.teacherNote)],
    });
  }

  return slides;
}

export function lessonFlowToWorksheetPdfDocument(lesson, options = {}) {
  const sheet = options.sheet ?? "student";
  const includeAnswerKey = options.includeAnswerKey ?? true;
  const questions = Array.isArray(lesson?.questions) ? lesson.questions : [];

  if (sheet === "teacher") {
    return {
      layout: "worksheet",
      filename: sanitizeFilename(lesson?.title, "teacher-sheet"),
      title: coerceText(lesson?.title) || "Teacher Sheet",
      subject: coerceText(lesson?.curriculumLabel),
      author: "Tema Quick Sheets",
      slides: [
        ...buildTeacherAppendixSlides(lesson),
        ...(includeAnswerKey ? buildAnswerKeySlides(lesson) : []),
      ],
    };
  }

  const slides = [
    {
      kind: "worksheet-header",
      title: coerceText(lesson?.title) || "Worksheet",
      curriculumLabel: coerceText(lesson?.curriculumLabel),
      subtitle: coerceText(lesson?.subtitle),
      questionCount: questions.length,
    },
    ...questions.map((question) => buildQuestionSlide(question)),
  ];

  return {
    layout: "worksheet",
    filename: sanitizeFilename(lesson?.title, "worksheet"),
    title: coerceText(lesson?.title) || "Worksheet",
    subject: coerceText(lesson?.curriculumLabel),
    author: "Tema Quick Sheets",
    slides,
  };
}
